import classNames from "classnames";
import React, { type ReactNode } from "react";
import { NATIVE_INVOICE_PAGE_SIZE_PX } from "@common/helpers/nativeInvoicePagination";
import { FinancialDocument } from "@common/types/financialDocument";
import { Loader } from "@design-system/Loader/Loader";
import { FinancialDocumentPreview } from "@components/documents/FinancialDocumentDetails/FinancialDocumentPreview";
import { useDocumentPreviewData } from "@components/documents/FinancialDocumentDetails/useDocumentPreviewData";
import { DEFAULT_NATIVE_INVOICE_PAGE_SIZE } from "@components/invoices/NativeInvoiceDrawer/nativeInvoiceFormTypes";
import previewStyles from "@components/invoices/NativeInvoiceDrawer/InvoiceDocumentPreview.module.scss";
import frameStyles from "@components/invoices/invoicePaperFrame.module.scss";
import { styleWithVars } from "@lib/cssVars";

interface Props {
    financialDocument: Pick<FinancialDocument, "id" | "fileName">;
    fallback: ReactNode;
}

export const AccrualDraftAttachedFilePreview: React.FC<Props> = ({
    financialDocument,
    fallback,
}) => {
    const logicalPaper =
        NATIVE_INVOICE_PAGE_SIZE_PX[DEFAULT_NATIVE_INVOICE_PAGE_SIZE];
    const {
        isFetching,
        isError,
        imagePreviewAvailable,
        pdfPreviewAvailable,
        previewUrl,
    } = useDocumentPreviewData(financialDocument, { retry: false });
    const showPage =
        previewUrl !== null && (pdfPreviewAvailable || imagePreviewAvailable);
    const viewportClassName = classNames(
        previewStyles.paperScaleViewport,
        previewStyles.paperScaleViewportFitHeight,
        previewStyles.attachedFileViewport,
    );

    if (isError) {
        return fallback;
    }

    if (!showPage) {
        if (isFetching) {
            return (
                <div className={viewportClassName}>
                    <Loader />
                </div>
            );
        }
        return (
            <FinancialDocumentPreview
                financialDocument={financialDocument}
                noPadding
            />
        );
    }

    return (
        <div className={viewportClassName}>
            <div
                className={classNames(
                    frameStyles.paperChrome,
                    previewStyles.attachedPaper,
                )}
                style={styleWithVars({
                    "--paper-width": logicalPaper.width,
                    "--paper-height": logicalPaper.height,
                })}
            >
                {pdfPreviewAvailable ? (
                    <iframe
                        title={financialDocument.fileName}
                        className={classNames(
                            frameStyles.documentFrame,
                            previewStyles.attachedFrame,
                        )}
                        src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                    />
                ) : (
                    <img
                        alt={financialDocument.fileName}
                        src={previewUrl}
                        className={classNames(
                            frameStyles.documentFrame,
                            previewStyles.attachedFrame,
                        )}
                    />
                )}
            </div>
        </div>
    );
};
